import * as React from 'react';
import {useHistory} from 'react-router-dom';
import roleRoutes from 'routes/admin.routes';
import NotAuthorized from 'components/notAuthorized';
import {useFindQuery, useDeleteMutation} from 'features/roles/rolesApi';
import {TableActions} from 'components/tableActions';
import {
  Box,
  Button,
  Cluster,
  Cover,
  Heading,
  ITableCol,
  Notification,
  Paragraph,
  Table,
  TdLink,
  Template,
} from '@pluto-tv/assemble';
import {IRole} from 'models/roles';
import {useAppPermissions} from 'app/permissions';
import {withThousandsSeparator} from 'utils/thousands-separator';
import {getPrefixedUrl} from 'routes';

const ROWS_PER_PAGE = 25;

export default (): JSX.Element => {
  const history = useHistory();
  const [page, setPage] = React.useState(0);
  const [allRoles, setAllRoles] = React.useState<IRole[]>([]);
  const [lazyLoading, setLazyLoading] = React.useState<boolean>(false);
  const {ableTo, permissions} = useAppPermissions();
  const [deleteRole] = useDeleteMutation();

  if (!ableTo('ROLE_VIEW')) {
    return <NotAuthorized />;
  }

  const {
    data: roles,
    isFetching,
    refetch,
  } = useFindQuery(
    {
      limit: ROWS_PER_PAGE,
      offset: page * ROWS_PER_PAGE,
      sort: '_id:dsc',
    },
    {refetchOnMountOrArgChange: true},
  );

  const handleCreate = () => {
    history.push(roleRoutes.paths.roleEditPage.replace(':id', 'new'));
  };

  const handleEdit = (role: IRole) => {
    history.push(roleRoutes.paths.roleEditPage.replace(':id', role._id));
  };

  const handleDelete = async (role: IRole) => {
    if (!ableTo('ROLE_DELETE')) return;
    await deleteRole(role._id).unwrap();
    refetch();
  };

  React.useEffect(() => {
    setLazyLoading(isFetching);
  }, [isFetching]);

  React.useEffect(() => {
    if (roles?.data) {
      setAllRoles(prevRoles => {
        const uniqueRoles = page === 0 ? [] : [...prevRoles];
        roles.data.forEach(newRole => {
          if (!uniqueRoles.some(existing => existing._id === newRole._id)) {
            uniqueRoles.push(newRole);
          }
        });
        return uniqueRoles;
      });
    }
  }, [roles?.data, page]);

  const onLazyLoad = React.useCallback(() => {
    if (!isFetching && allRoles.length < (roles?.metadata.totalCount || 0)) {
      setPage(prevPage => prevPage + 1);
    }
  }, [isFetching, allRoles.length, roles?.metadata.totalCount]);

  return (
    <>
      <Cover scrolling={true} gutter='large' coverTemplateHeight='100%' padding={{mobile: 'medium', wide: 'large'}}>
        <Template label='header'>
          <Cluster justify='space-between' align='center' space='medium'>
            <Cluster align='end' space='small'>
              <Heading level='h1'>Roles</Heading>
              <Paragraph color='secondary'>{withThousandsSeparator(roles?.metadata.totalCount || 0)} Items</Paragraph>
            </Cluster>
            <Button type='primary' onClick={() => handleCreate()} permission={permissions.ROLE_CREATE}>
              + New Role
            </Button>
          </Cluster>
        </Template>
        <Template label='cover'>
          <Box
            background='pewter'
            borderTop={true}
            borderSize='0.125rem'
            borderColor='cavern'
            paddingTop={(allRoles?.length as number) > 0 ? 'xsmall' : 'medium'}
            paddingBottom='none'
            paddingX='large'
            fullHeight={true}
          >
            <Table
              id='rolesTable'
              loading={isFetching && page === 0}
              fixedHeader={true}
              wrapContent={true}
              lazyLoading={page !== 0 && lazyLoading}
              onLazyLoad={onLazyLoad}
              lazyLoadingMsg='Loading more roles...'
              cols={[
                {
                  label: 'Role Name',
                  transform: row => (
                    <TdLink
                      title={row.name}
                      row={row}
                      url={getPrefixedUrl(roleRoutes.paths.roleEditPage.replace(':id', row._id))}
                      onClick={handleEdit}
                    />
                  ),
                },
                {
                  label: 'Description',
                  field: 'description',
                },
                ...(ableTo('ROLE_EDIT') || ableTo('ROLE_DELETE')
                  ? [
                      {
                        label: 'Actions',
                        colWidth: '6.25rem',
                        transform: row => (
                          <TableActions
                            row={row}
                            icons={ableTo('ROLE_EDIT') ? ['edit'] : []}
                            deleteOption={ableTo('ROLE_DELETE')}
                            onClick={(row, icon) => {
                              switch (icon) {
                                case 'edit':
                                  handleEdit(row);
                                  break;
                                case 'delete':
                                  handleDelete(row);
                                  break;
                                default:
                              }
                            }}
                          />
                        ),
                      } as ITableCol<IRole>,
                    ]
                  : []),
              ]}
              rows={allRoles}
            >
              <Template label='empty'>
                <Notification type='warning'>There are no roles currently available.</Notification>
              </Template>
            </Table>
          </Box>
        </Template>
      </Cover>
    </>
  );
};
